import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import type { ITenant } from '@experiences/interfaces';
import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import {
    ApDropdown,
    ApDropdownItem,
    ApTextField,
} from '@uipath/portal-shell-react';
import React, { useCallback } from 'react';
import type {
    Control,
    ErrorOption,
    FieldPath,
    FieldPathValue,
    SetValueConfig,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type {
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    KnownProductFeatureNames,
    ModelsConfigurationOption,
} from '../../../../services/llm-gateway/LlmGateway.types';
import {
    getProductFeatureGaTranslationKey,
    getProductFeatureTranslationKey,
    getProductTranslationKey,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import {
    LabelWithTooltip,
    type TooltipProps,
} from '../../subcomponents/LabelWithTooltip';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';

type AddEditConfigurationHeaderSectionProps = {
    control: Control<AddEditConfigurationFormData>;
    resetField: (name: FieldPath<AddEditConfigurationFormData>) => void;
    setValue:
    (name: FieldPath<AddEditConfigurationFormData>, value: FieldPathValue<AddEditConfigurationFormData, FieldPath<AddEditConfigurationFormData>>, options?: SetValueConfig) => void;
    setError: (name: (FieldPath<AddEditConfigurationFormData> | `root.${string}` | 'root'), error: ErrorOption, options?: { shouldFocus: boolean }) => void;
    product?: string;
    notifyFieldChanges: () => void;
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    replaceableLlms: Record<string, Record<string, string[]>>;
    isEditMode?: boolean;
    tenants?: ITenant[];
    onTenantChange?: (tenantId: string) => void;
    tenantTooltip?: TooltipProps;
    productTooltip?: TooltipProps;
    featureTooltip?: TooltipProps;
};

export const AddEditConfigurationHeaderSection: React.FC<AddEditConfigurationHeaderSectionProps> = ({
    control,
    resetField,
    setValue,
    setError,
    product,
    notifyFieldChanges,
    productConfigurations,
    replaceableLlms,
    isEditMode = false,
    tenants,
    onTenantChange,
    tenantTooltip,
    productTooltip,
    featureTooltip,
}: AddEditConfigurationHeaderSectionProps) => {
    const { formatMessage: translate } = useIntl();
    const tenantSelectable = (tenants?.length ?? 0) > 1;

    // GA flags
    const isScreenPlayGaEnabled = useFeatureFlagValue(Features.AiTrustLayerByoLlmScreenPlayGa.name);
    const isSemanticTargetingGaEnabled = useFeatureFlagValue(Features.AiTrustLayerByoLlmSemanticTargetingGa.name);

    const getFeatureLabel = useCallback((feature: string): string => {
        if ((feature.toUpperCase() === KnownProductFeatureNames.UiaScreenPlay && isScreenPlayGaEnabled) ||
            (feature.toUpperCase() === KnownProductFeatureNames.UiaSemanticTargeting && isSemanticTargetingGaEnabled)) {
            return translate({ id: getProductFeatureGaTranslationKey(feature) });
        }
        return translate({ id: getProductFeatureTranslationKey(feature) });
    }, [ isScreenPlayGaEnabled, isSemanticTargetingGaEnabled, translate ]);

    return (<UiStack
        direction='row'
        gap={SpacingToken.XL}>
        <UiStack direction='column'>
            <LabelWithTooltip
                label={translate({ id: 'CLIENT_TENANT' })}
                required={tenantSelectable}
                tooltip={tenantTooltip ?? {
                    translationKey: 'CLIENT_BYO_LLM_TOOLTIP_TENANT',
                    placement: 'top',
                }}
                dataCy="tenant-dropdown-label-with-tooltip"
            />
            {tenantSelectable
                ? <ApDropdown
                    style={{ width: '300px' }}
                    required
                    onSelectedValueChanged={(event) => onTenantChange?.(event.detail as string)}
                    data-cy="llm-configuration-tenant-dropdown">
                    {tenants?.map(t => (
                        <ApDropdownItem
                            key={t.id}
                            value={t.id}
                            label={t.name} />
                    ))}
                </ApDropdown>
                : <ApTextField
                    style={{ width: '300px' }}
                    value={tenants?.[0]?.name ?? ''}
                    readOnly
                    data-cy="llm-configuration-tenant-dropdown"
                />}
        </UiStack>
        <Controller
            name="product"
            control={control}
            rules={{ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }}
            render={({ field }) => (
                <UiStack direction='column'>
                    <LabelWithTooltip
                        label={translate({ id: 'CLIENT_BYO_LLM_PRODUCT' })}
                        required
                        tooltip={productTooltip ?? {
                            translationKey: 'CLIENT_BYO_LLM_TOOLTIP_PRODUCT',
                            placement: 'top',
                        }}
                        dataCy="product-dropdown-label-with-tooltip"
                    />
                    <ApDropdown
                        style={{ width: '300px' }}
                        data-cy="product-dropdown"
                        required
                        disabled={isEditMode}
                        selectedValue={field.value}
                        onSelectedValueChanged={(event) => {
                            const selectedProduct = event.detail?.toString();
                            const featureOptions = Object.keys(replaceableLlms[selectedProduct ?? ''] ?? {});

                            field.onChange(selectedProduct);

                            if (featureOptions.length === 1) {
                                const onlyFeature = featureOptions[0];

                                // Set required dependent fields as if the user selected the feature manually
                                setValue('feature', onlyFeature);
                                setValue('configurationMode', 'replace');
                                setValue('useAiGateway', false);

                                if (productConfigurations[selectedProduct]?.[onlyFeature]?.modelsConfigurationOption === ModelsConfigurationOption.AllModels) {
                                    setValue('llmConfigurations', (replaceableLlms[selectedProduct]?.[onlyFeature] ?? [])
                                        .reduce((acc, llm) => {
                                            acc[llm] = { desiredModel: llm };
                                            return acc;
                                        }, {} as Record<string, Partial<ByoLlmConfiguration>>));
                                } else {
                                    resetField('llmConfigurations');
                                }
                            } else {
                                resetField('feature');
                                resetField('configurationMode');
                                setValue('useAiGateway', false);
                                resetField('llmConfigurations');
                            }

                            // Manually set error as field controller doesn't exist at this point
                            setError('llmConfigurations', {
                                type: 'validate',
                                message: translate({ id: 'CLIENT_BYO_LLM_CONFIGURE_LLM_CONFIGURATIONS_INCOMPLETE_ERROR' }
                                ),
                            });

                            notifyFieldChanges();
                        }}>
                        {Object.keys(replaceableLlms).map(productOption => (
                            <ApDropdownItem
                                label={translate({ id: getProductTranslationKey(productOption) })}
                                value={productOption}
                                key={`ap-dropdown-item-${productOption}`}
                            />))}
                    </ApDropdown>
                </UiStack>
            )}
        />
        <Controller
            name="feature"
            control={control}
            rules={{ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }}
            render={({ field }) => (
                <UiStack direction='column'>
                    <LabelWithTooltip
                        label={translate({ id: 'CLIENT_BYO_LLM_FEATURE' })}
                        required
                        tooltip={featureTooltip ?? {
                            translationKey: 'CLIENT_BYO_LLM_TOOLTIP_FEATURE',
                            placement: 'top',
                        }}
                        dataCy="feature-dropdown-label-with-tooltip"
                    />
                    <ApDropdown
                        style={{ width: '300px' }}
                        data-cy="feature-dropdown"
                        required
                        disabled={!product || isEditMode}
                        selectedValue={field.value}
                        onSelectedValueChanged={(event) => {
                            field.onChange(event.detail);
                            setValue('configurationMode', 'replace');
                            setValue('useAiGateway', false);

                            const selectedFeature = event.detail?.toString();
                            const productConfig = product ? productConfigurations[product]?.[selectedFeature ?? ''] : undefined;
                            const modelsForFeature = product ? replaceableLlms[product]?.[selectedFeature ?? ''] : undefined;

                            if (productConfig?.modelsConfigurationOption === ModelsConfigurationOption.AllModels && modelsForFeature) {
                                setValue('llmConfigurations', modelsForFeature
                                    .reduce((acc, llm) => {
                                        acc[llm] = {
                                            desiredModel: llm,
                                            defaultModel: llm,
                                        } as Partial<ByoLlmConfiguration>;
                                        return acc;
                                    }, {} as Record<string, Partial<ByoLlmConfiguration>>));
                            } else {
                                resetField('llmConfigurations');
                            }

                            // Manually set error as field controller doesn't exist at this point
                            setError('llmConfigurations', {
                                type: 'validate',
                                message: translate({ id: 'CLIENT_BYO_LLM_CONFIGURE_LLM_CONFIGURATIONS_INCOMPLETE_ERROR' }
                                ),
                            });

                            notifyFieldChanges();
                        }}>
                        {Object.keys(replaceableLlms[product ?? ''] ?? {}).map(featureOption => (
                            <ApDropdownItem
                                label={getFeatureLabel(featureOption)}
                                value={featureOption}
                                key={`ap-dropdown-item-${featureOption}`}
                            />))}
                    </ApDropdown>
                </UiStack>
            )}
        />
    </UiStack>);
};
